// /home/szymon/kreator2/frontend/src/components/ClientPdfEditor.js
// v12.0.0 – Dodano wymuszenie publicMode = true w pdfData

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Editor from "./Editor";
import AiProcessingOverlay from "./common/AiProcessingOverlay"; // Import nowego komponentu
import { Info } from 'lucide-react'; // Import ikony Info (może być potrzebna, jeśli tu też dodamy przełącznik)

export default function ClientPdfEditor({ backendUrl, tooltipsEnabled, setTooltipsEnabled }) { // Odbieramy nowe propsy
  const params = useParams();
  const navigate = useNavigate();

  // Decode and clean keys immediately after getting them from params
  const rawPublicKey = params.publicKey || '';
  let cleanedPublicKey = null;
  try {
      cleanedPublicKey = decodeURIComponent(rawPublicKey).replace(/[^a-zA-Z0-9]/g, '');
  } catch (e) {
      console.error("Error decoding or cleaning publicKey:", rawPublicKey, e);
      cleanedPublicKey = rawPublicKey.replace(/[^a-zA-Z0-9]/g, ''); // Fallback cleaning
  }

  const rawDownloadKey = params.downloadKey || '';
  let cleanedDownloadKey = null;
  try {
      cleanedDownloadKey = decodeURIComponent(rawDownloadKey).replace(/[^a-zA-Z0-9]/g, '');
  } catch (e) {
      console.error("Error decoding or cleaning downloadKey:", rawDownloadKey, e);
      cleanedDownloadKey = rawDownloadKey.replace(/[^a-zA-Z0-9]/g, ''); // Fallback cleaning
  }


  const [pdfData, setPdfData] = useState(null);
  const [fileObjId, setFileObjId] = useState(null);
  const [orderEmailFromData, setOrderEmailFromData] = useState(''); // Nowy stan dla emaila z zamówienia
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acceptingProject, setAcceptingProject] = useState(false); // Stan ładowania akceptacji
  const [isAiProcessing, setIsAiProcessing] = useState(false); // Nowy stan dla overlay'a AI

  // Flaga: czy plik jest „zaakceptowany”
  const [isAccepted, setIsAccepted] = useState(false);
  const [showAcceptConfirmation, setShowAcceptConfirmation] = useState(false); // Stan dla popupu potwierdzenia akceptacji

  // --- Funkcja akceptacji projektu ---
  const handleAcceptProject = async () => {
    if (acceptingProject || isAccepted) return; // Nie akceptuj ponownie lub gdy trwa proces

    setAcceptingProject(true);
    setError(""); // Resetuj błąd przed próbą

    try {
      // 1. Pobierz ID statusu "zaakceptowany"
      const stUrl = `${backendUrl}/public/statuses`;
      console.log("[ClientPdfEditor][Accept] => GET Statuses =>", stUrl);
      const stRes = await fetch(stUrl);
      if (!stRes.ok) {
        throw new Error(`Błąd pobierania statusów (status=${stRes.status})`);
      }
      const statuses = await stRes.json();
      const acceptedStatus = statuses.find(
        (s) => s.name && s.name.toLowerCase().includes("zaakceptowan") // Bardziej elastyczne wyszukiwanie
      );

      if (!acceptedStatus) {
        throw new Error("Nie znaleziono statusu 'zaakceptowany'");
      }
      const acceptedStatusId = acceptedStatus.id;

      // 2. Wyślij żądanie PATCH, aby zaktualizować status pliku
      const patchUrl = `${backendUrl}/client/orders/${cleanedPublicKey}/files/${fileObjId}/status`;
      console.log("[ClientPdfEditor][Accept] => PATCH Status =>", patchUrl);
      const patchRes = await fetch(patchUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ statusId: acceptedStatusId }),
      });

      if (!patchRes.ok) {
        const errorData = await patchRes.text(); // Spróbuj odczytać treść błędu
        throw new Error(
          `Błąd aktualizacji statusu pliku (status=${patchRes.status}). Odpowiedź: ${errorData}`
        );
      }

      // 3. Zaktualizuj stan lokalny
      setIsAccepted(true);
      setShowAcceptConfirmation(true); // Pokaż popup potwierdzający

    } catch (ex) {
      console.error("[ClientPdfEditor][Accept] => Error:", ex);
      setError(`Błąd podczas akceptacji projektu: ${ex.message}`);
      alert(`Błąd podczas akceptacji projektu: ${ex.message}`); // Pokaż błąd użytkownikowi
    } finally {
      setAcceptingProject(false);
    }
  };


  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      setLoading(true);
      setError("");

      try {
        // (1) Pobierz dane zamówienia (public) using cleaned key
        const orderUrl = `${backendUrl}/client/orders/${cleanedPublicKey}`;
        console.log("[ClientPdfEditor] => GET =>", orderUrl);

        const orderRes = await fetch(orderUrl);
        if (!orderRes.ok) {
          throw new Error(
            `Błąd pobierania zamówienia public (status=${orderRes.status})`
          );
        }
        const orderData = await orderRes.json();
        if (!isMounted) return;

        console.log('[ClientPdfEditor] Fetched orderData:', orderData); // Dodajemy logowanie orderData

        // Ustaw e-mail zamówienia, jeśli jest dostępny
        if (orderData && orderData.customOrderId) { // Używamy customOrderId zgodnie z informacją od użytkownika
          setOrderEmailFromData(orderData.customOrderId);
        } else if (orderData && orderData.client_email) { // Poprzednie założenie
          setOrderEmailFromData(orderData.client_email);
        } else if (orderData && orderData.email) { // Alternatywna nazwa pola
          setOrderEmailFromData(orderData.email);
        }


        // Znajdź plik z matching cleaned downloadKey
        const foundFile = (orderData.files || []).find(
          (f) => f.downloadKey === cleanedDownloadKey
        );
        if (!foundFile) {
          throw new Error("Nie znaleziono pliku w orderData.files");
        }
        setFileObjId(foundFile.id);

        // Sprawdź, czy status jest „zaakceptowany”
        const fileStatusId = foundFile.statusId || null;
        if (fileStatusId) {
          const stUrl = `${backendUrl}/public/statuses`;
          console.log("[ClientPdfEditor] => GET =>", stUrl);

          const stRes = await fetch(stUrl);
          if (!stRes.ok) {
            throw new Error(
              `Błąd pobierania listy statusów (public), status=${stRes.status}`
            );
          }
          const statuses = await stRes.json();
          if (!isMounted) return;

          const st = statuses.find((x) => x.id === fileStatusId);
          if (st && st.name) {
            const stLower = st.name.toLowerCase();
            if (stLower === "zaakceptowany" || stLower === "zaakceptowane") {
              setIsAccepted(true);
            }
          }
        }

        // (2) Pobierz metadane PDF using cleaned keys
        const metaUrl = `${backendUrl}/client/orders/${cleanedPublicKey}/pdf-editor/${cleanedDownloadKey}`;
        console.log("[ClientPdfEditor] => GET =>", metaUrl);

        const metaRes = await fetch(metaUrl);
        if (!metaRes.ok) {
          throw new Error(
            `Błąd pobierania metadanych (public), status=${metaRes.status}`
          );
        }
        const metaData = await metaRes.json();
        if (!metaData.pdfData || !metaData.fileObjId) {
          throw new Error("Niepoprawne dane meta (brak pdfData/fileObjId)");
        }

        // Wymuszamy tryb publiczny, jeśli backend nie ustawił
        metaData.pdfData.publicMode = true;

        setPdfData(metaData.pdfData);
        setFileObjId(metaData.fileObjId);
        setError("");

        // Usunięto sprawdzanie personalizacji stąd

      } catch (ex) {
        console.error("[ClientPdfEditor] =>", ex);
        if (isMounted) {
          setError(ex.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  // }, [backendUrl, publicKey, downloadKey]); // Original dependencies
  }, [backendUrl, cleanedPublicKey, cleanedDownloadKey]); // Use cleaned keys in dependency array

  // Usunięto useEffect sprawdzający personalizację - flaga będzie odczytywana bezpośrednio z pdfData

  const handleGoBack = () => {
    // Use cleaned key for navigation
    navigate(`/client/orders/${cleanedPublicKey}`);
  };

  if (loading) {
    return <div style={{ padding: 20 }}>Wczytywanie edytora PDF...</div>;
  }
  if (error) {
    return (
      <div style={{ padding: 20, color: "red" }}>
        <p>ERROR: {error}</p>
        {/* Use cleaned key for navigation in error case */}
        <button onClick={() => navigate(`/client/orders/${cleanedPublicKey}`)}>
          Wróć
        </button>
      </div>
    );
  }
  if (!pdfData || !fileObjId) {
    return (
      <div style={{ padding: 20 }}>
        <p>Brak pdfData lub fileObjId.</p>
        {/* Use cleaned key for navigation in missing data case */}
        <button onClick={() => navigate(`/client/orders/${cleanedPublicKey}`)}>
          Wróć
        </button>
      </div>
    );
  }

  // Odczytujemy flagę bezpośrednio z pdfData
  const isPersonalizedProject = !!pdfData?.isPersonalized;
  console.log("[ClientPdfEditor] Using isPersonalized flag from pdfData:", isPersonalizedProject);

  // Rendering Edytora z isAccepted i flagą isPersonalizedProject
  return (
    <div style={{ width: "100%", height: "100vh" }}>
      <AiProcessingOverlay isActive={isAiProcessing} />
      <Editor
        backendUrl={backendUrl}
        pdfData={pdfData}
        projectId={fileObjId} // Przekazujemy fileObjId jako projectId do Editora
        onGoBack={handleGoBack}
        orderPublicKey={cleanedPublicKey} // Pass cleaned key
        downloadKey={cleanedDownloadKey} // Pass cleaned key
        isAccepted={isAccepted}
        onAcceptProject={handleAcceptProject} // Przekazanie nowej funkcji
        tooltipsEnabled={tooltipsEnabled} // Przekazanie propa do Editor
        setTooltipsEnabled={setTooltipsEnabled} // Przekazanie propa do Editor
        isPersonalizedProject={isPersonalizedProject} // Przekaż flagę boolean
        publicMode={true} // ClientPdfEditor jest zawsze w trybie publicznym
        orderEmail={orderEmailFromData} // Przekazujemy email zamówienia
        setIsAiProcessing={setIsAiProcessing} // Przekazujemy funkcję do zarządzania overlayem
        onEmailUpdated={setOrderEmailFromData} // Callback do aktualizacji emaila
      />

      {/* Popup potwierdzenia akceptacji */}
      {showAcceptConfirmation && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999 // Upewnij się, że jest na wierzchu
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "24px", // Trochę więcej paddingu
              borderRadius: "8px", // Bardziej zaokrąglone rogi
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)", // Wyraźniejszy cień
              minWidth: 320, // Minimalna szerokość
              textAlign: "center",
              borderTop: "4px solid #48bb78" // Zielony akcent na górze (Tailwind green-500)
            }}
          >
            <span className="material-icons" style={{ fontSize: 48, color: '#48bb78', marginBottom: '16px' }}>
              check_circle_outline {/* Ikona sukcesu */}
            </span>
            <h2 style={{ marginBottom: "16px", fontSize: "1.25rem", color: "#2d3748" }}> {/* Tailwind gray-800 */}
              Projekt zaakceptowany!
            </h2>
            <p style={{ marginBottom: "24px", color: "#4a5568", lineHeight: '1.5' }}> {/* Tailwind gray-600 */}
              Twój projekt został pomyślnie zaakceptowany. Aby złożyć zamówienie, przejdź do panelu zamówienia i kliknij odpowiedni przycisk. Pamiętaj, że po akceptacji dalsze zmiany w projekcie może wprowadzić tylko nasz grafik.
            </p>
            {/* Dodano przycisk do panelu zamówienia */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
              <button
                onClick={handleGoBack} // Użyj istniejącej funkcji nawigacji
                style={{
                  // Styl podobny do przycisku "Panel zamówienia" w TopBar
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "10px 20px",
                  border: "1px solid #4299e1",
                  borderRadius: "6px",
                  fontSize: "1rem",
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  backgroundColor: "#4299e1", // primaryButtonStyle background
                  color: "#ffffff", // primaryButtonStyle color
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2b6cb0'} // Ciemniejszy niebieski hover
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#4299e1'}
              >
                <span className="material-icons" style={{ marginRight: '8px', fontSize: '20px' }}>dashboard</span> {/* Ikona panelu */}
                Panel zamówienia
              </button>
              <button
                onClick={() => setShowAcceptConfirmation(false)} // Przycisk OK pozostaje
                style={{
                  // Styl przycisku OK
                  border: "none",
                  background: "#6c757d", // Szary przycisk (secondary)
                  color: "#fff",
                  borderRadius: "6px",
                  cursor: "pointer",
                  padding: "10px 20px",
                  fontSize: "1rem",
                  fontWeight: 500,
                  transition: "background 0.2s ease"
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#5a6268'} // Ciemniejszy szary hover
                onMouseOut={(e) => e.currentTarget.style.background = '#6c757d'}
              >
                OK
              </button>
            </div>
            {/* Usunięto stary przycisk OK - ten komentarz jest już niepotrzebny */}
          </div>
        </div>
      )}
    </div>
  );
}
